import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  MapPin, Briefcase, GraduationCap, Star, Globe, Award, ArrowLeft,
  Loader2, Download, CheckCircle, Edit2, ExternalLink, Users
} from "lucide-react";
import { useAuth, getAuthHeaders } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { CandidateAvatar } from "../components/CandidateAvatar";
import { CompletenessRing, TimelineItem, CandidateProfileContent } from "../components/CandidateProfileSections";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Re-exported for backward compatibility — ProfessionalProfileTab.jsx and
// CompanyPreviewPage.jsx import TimelineItem from this module.
export { CompletenessRing, TimelineItem };

/* ── Main Page ────────────────────────────────────────────────────────────── */
export default function ProfessionalProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const isOwnProfile = user?.user_id === id;

  useEffect(() => {
    const load = async () => {
      try {
        const endpoint = isOwnProfile ? `${API}/my/complete-profile` : `${API}/profile/${id}`;
        const headers  = isOwnProfile ? getAuthHeaders() : {};
        const res = await axios.get(endpoint, { headers });
        setProfile(res.data);
      } catch { navigate("/"); }
      finally { setLoading(false); }
    };
    load();
  }, [id, isOwnProfile, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 text-[#028CF8] animate-spin" />
      </div>
    );
  }
  if (!profile) return null;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="professional-profile-page">

      {/* ── Hero Header ── */}
      <section className="hero-infocity py-10">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white hover:underline text-sm mb-5 transition-colors font-poppins">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            {/* Avatar */}
            <CandidateAvatar name={profile.name} picture={profile.picture} size={80} rounded="xl" />

            <div className="flex-1 min-w-0">
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginBottom: "4px" }}>
                <h1 className="font-poppins text-2xl md:text-3xl font-bold text-white">{profile.name}</h1>
                {profile.open_to_work && (
                  <span style={{ fontSize: "11px", fontWeight: 700, padding: "3px 10px", borderRadius: "999px", background: "rgba(16,185,129,0.2)", border: "1px solid rgba(16,185,129,0.4)", color: "#6EE7B7", fontFamily: "Poppins, sans-serif" }}>
                    ✓ Open to Work
                  </span>
                )}
              </div>
              {profile.title && <p style={{ fontSize: "16px", fontWeight: 600, color: "white", fontFamily: "Poppins, sans-serif", marginBottom: "6px" }}>{profile.title}</p>}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "14px" }}>
                {profile.location && (
                  <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "13px", color: "white", fontFamily: "Poppins, sans-serif" }}>
                    <MapPin style={{ width: 13, height: 13 }} /> {profile.location}
                  </span>
                )}
                {profile.years_experience > 0 && (
                  <span style={{ fontSize: "13px", color: "white", fontFamily: "Poppins, sans-serif" }}>
                    <Briefcase style={{ width: 12, height: 12, display: "inline", marginRight: "4px" }} />
                    {profile.years_experience}+ years exp
                  </span>
                )}
                {profile.linkedin && (
                  <a href={profile.linkedin} target="_blank" rel="noopener noreferrer"
                    style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "13px", color: "white", textDecoration: "none" }}>
                    <ExternalLink style={{ width: 12, height: 12 }} /> LinkedIn
                  </a>
                )}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", flexShrink: 0 }}>
              {isOwnProfile && (
                <>
                  <Link to="/cv-builder"
                    style={{ display: "flex", alignItems: "center", gap: "6px", height: "38px", padding: "0 16px", background: "white", color: "#028CF8", borderRadius: "10px", fontSize: "12px", fontWeight: 700, fontFamily: "Poppins, sans-serif", textDecoration: "none" }}>
                    <Edit2 style={{ width: 13, height: 13 }} /> Edit Profile
                  </Link>
                  <a href={`${API}/cv/download-pdf`} target="_blank" rel="noreferrer"
                    style={{ display: "flex", alignItems: "center", gap: "6px", height: "38px", padding: "0 16px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.25)", color: "white", borderRadius: "10px", fontSize: "12px", fontWeight: 600, fontFamily: "Poppins, sans-serif", textDecoration: "none" }}>
                    <Download style={{ width: 13, height: 13 }} /> {t("profProfile.downloadCV")}
                  </a>
                </>
              )}
              {!isOwnProfile && user?.role === "employer" && (
                <div style={{ height: "38px", padding: "0 16px", background: "white", color: "#028CF8", borderRadius: "10px", fontSize: "12px", fontWeight: 700, fontFamily: "Poppins, sans-serif", display: "flex", alignItems: "center", gap: "6px" }}>
                  <Users style={{ width: 13, height: 13 }} /> Candidate
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Profile completeness bar ── */}
      {isOwnProfile && (
        <div style={{ background: "white", borderBottom: "1px solid #E5E7EB" }}>
          <div className="container mx-auto px-4 md:px-6 lg:px-8 py-4">
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <CompletenessRing score={profile.completeness || 0} size={56} />
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                  <span style={{ fontSize: "13px", fontWeight: 600, color: "#0F172A", fontFamily: "Poppins, sans-serif" }}>
                    {t("profProfile.completeness")}: {profile.completeness || 0}%
                  </span>
                  {profile.completeness < 100 && (
                    <Link to="/cv-builder" style={{ fontSize: "12px", fontWeight: 600, color: "#028CF8", fontFamily: "Poppins, sans-serif", textDecoration: "none" }}>
                      {t("profProfile.completePrompt")}
                    </Link>
                  )}
                </div>
                <div style={{ height: 6, background: "#F1F5F9", borderRadius: "999px", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${profile.completeness || 0}%`, background: profile.completeness >= 80 ? "#10B981" : profile.completeness >= 60 ? "#028CF8" : "#64748B", borderRadius: "999px", transition: "width 0.5s ease" }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Content ── */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
        <CandidateProfileContent profile={profile} t={t} />
      </div>
    </div>
  );
}
